import { ShieldCheck, BadgeCheck, RefreshCw, Loader2, KeyRound, ShieldQuestion, Copy, AlertTriangle, Atom } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Avatar } from "@/components/common/Avatar";
import { QemCredit } from "@/components/common/QemCredit";

// 16-hex fingerprint → grouped "XXXX XXXX XXXX XXXX" for readable side-by-side comparison.
const fmtFp = (fp) => (fp ? fp.toUpperCase().match(/.{1,4}/g).join(" ") : "");

// Group Cipher Lock verification (Phase 2b). A Circle has N members, so instead of one mutual safety
// number we show a per-member roster: each member's identity fingerprint, an ML-DSA-65 "signed" badge,
// and a web-local "Mark verified" toggle. Matches the DM verify trust model without a shared code.
export function GroupCipherDialog({
  open,
  onOpenChange,
  cipher,
  roster = [],
  membersInfo = {},
  verifiedMap = {},
  safetyNumber,
  codeVerified,
  codeStale,
  quantumSeeded,
  onVerifyCode,
  onVerifyMember,
  onRefresh,
  loadingRoster,
}) {
  const state = cipher?.state;
  const withBundle = roster.filter((m) => m.fp);
  const noBundle = roster.filter((m) => !m.fp);
  const verifiable = withBundle.filter((m) => !m.isMe);
  const verifiedCount = verifiable.filter((m) => verifiedMap[m.id]).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="group-cipherlock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-5 w-5 text-bloq-mint" /> Cipher Lock
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Post-quantum end-to-end encryption for this Circle.
          </DialogDescription>
        </DialogHeader>

        {state === "active" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-bloq-mint/30 bg-bloq-mint/10 p-3.5" data-testid="group-cipher-active">
              <ShieldCheck className="h-5 w-5 shrink-0 text-bloq-mint" />
              <div>
                <div className="text-sm font-semibold text-bloq-mint">Quantum-secured &amp; active</div>
                <div className="text-xs text-bloq-muted">ML-KEM-768 + X25519 group key · XChaCha20-Poly1305</div>
              </div>
            </div>

            {quantumSeeded && (
              <div className="flex items-center gap-2.5 rounded-xl border border-bloq-purple/30 bg-bloq-purple/10 px-3 py-2.5" data-testid="quantum-seeded-pill">
                <Atom className="h-4 w-4 shrink-0 text-bloq-purple" />
                <div className="text-xs leading-snug">
                  <span className="font-semibold text-bloq-purple">QRNG Active</span>
                  <span className="text-bloq-muted"> · your identity keys were generated with QeM quantum entropy</span>
                </div>
              </div>
            )}

            {safetyNumber && (
              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
                  <KeyRound className="h-3.5 w-3.5" /> Group safety code
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(safetyNumber); toast.success("Safety code copied"); }}
                  className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-bloq-bg px-4 py-3 font-mono text-lg tracking-widest transition-colors hover:border-bloq-mint/30"
                  data-testid="group-safety-code"
                >
                  <span>{safetyNumber}</span>
                  <Copy className="h-4 w-4 text-bloq-muted" />
                </button>
                <p className="mt-1 text-[11px] text-bloq-muted">
                  Identical on every member’s device. Compare it to confirm no one is intercepting this Circle.
                </p>
                {codeVerified ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-bloq-mint/10 px-3 py-2 text-xs font-semibold text-bloq-mint" data-testid="group-code-verified">
                    <BadgeCheck className="h-4 w-4" /> You’ve verified this Circle is secure
                  </div>
                ) : (
                  <>
                    {codeStale && (
                      <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-400" data-testid="group-code-stale">
                        <AlertTriangle className="h-4 w-4 shrink-0" /> The code changed since you last verified — compare &amp; re-confirm.
                      </div>
                    )}
                    <button
                      onClick={onVerifyCode}
                      className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-bloq-mint/40 px-3 py-2 text-xs font-bold text-bloq-mint transition-colors hover:bg-bloq-mint/10"
                      data-testid="group-code-verify-btn"
                    >
                      <ShieldCheck className="h-4 w-4" /> {codeStale ? "Re-verify this code" : "I’ve confirmed this code matches"}
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
                <ShieldCheck className="h-3.5 w-3.5" /> Per-member identity
              </div>
              <span className="text-[11px] font-semibold text-bloq-mint" data-testid="group-verify-progress">
                {verifiedCount}/{verifiable.length} verified
              </span>
            </div>

            {loadingRoster ? (
              <div className="flex justify-center py-6 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="group-verify-roster">
                {withBundle.map((m) => {
                  const info = membersInfo[m.id] || {};
                  const name = m.isMe ? "You" : info.display_name || info.username || "Member";
                  const isVerified = verifiedMap[m.id];
                  return (
                    <div key={m.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-bloq-s2 p-2.5" data-testid={`group-verify-member-${m.id}`}>
                      <Avatar user={info} size="sm" flair={false} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold">
                          <span className="truncate">{name}</span>
                          {m.signed && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-bloq-blue" title="Identity key signed (ML-DSA-65)" />}
                        </div>
                        <div className="font-mono text-[11px] tracking-wider text-bloq-muted" data-testid={`group-verify-fp-${m.id}`}>{fmtFp(m.fp)}</div>
                      </div>
                      {m.isMe ? (
                        <span className="shrink-0 rounded-full bg-white/8 px-2 py-1 text-[10px] font-bold uppercase text-bloq-muted">This device</span>
                      ) : isVerified ? (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-bloq-mint/15 px-2.5 py-1 text-[11px] font-bold text-bloq-mint" data-testid={`group-verified-${m.id}`}>
                          <BadgeCheck className="h-3.5 w-3.5" /> Verified
                        </span>
                      ) : (
                        <button
                          onClick={() => onVerifyMember?.(m.id, m.fp)}
                          className="shrink-0 rounded-full bloq-gradient px-3 py-1.5 text-[11px] font-bold text-bloq-bg transition-opacity hover:opacity-90"
                          data-testid={`group-verify-btn-${m.id}`}
                        >
                          Mark verified
                        </button>
                      )}
                    </div>
                  );
                })}
                {noBundle.map((m) => {
                  const info = membersInfo[m.id] || {};
                  const name = info.display_name || info.username || "Member";
                  return (
                    <div key={m.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-bloq-s2/50 p-2.5 opacity-70" data-testid={`group-verify-nobundle-${m.id}`}>
                      <Avatar user={info} size="sm" flair={false} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">{name}</div>
                        <div className="text-[11px] text-bloq-muted">Hasn't set up Cipher Lock yet</div>
                      </div>
                      <ShieldQuestion className="h-4 w-4 shrink-0 text-bloq-muted" />
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-[11px] text-bloq-muted">
              Compare a member's safety number in person (or over another trusted channel) to confirm no one
              is intercepting this Circle. A blue badge means their identity key is cryptographically signed.
            </p>

            {quantumSeeded && (
              <div className="flex justify-center border-t border-white/8 pt-3"><QemCredit /></div>
            )}
          </div>
        ) : state === "waiting" ? (
          <div className="space-y-3" data-testid="group-cipher-waiting">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">
              Your device needs the group key. Tap “Re-key Cipher Lock on this device” in the chat to heal it.
            </div>
            <button onClick={onRefresh} className="flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-2 text-xs font-semibold text-bloq-muted transition-colors hover:text-white" data-testid="group-cipher-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Check again
            </button>
          </div>
        ) : state === "available" ? (
          <div className="rounded-xl border border-white/8 bg-bloq-s2 p-4 text-sm text-bloq-muted" data-testid="group-cipher-available">
            Cipher Lock isn’t enabled for this Circle yet. The owner can turn it on from the chat to add
            post-quantum end-to-end encryption for every member.
          </div>
        ) : (
          <div className="space-y-3" data-testid="group-cipher-off">
            <div className="rounded-xl border border-white/8 bg-bloq-s2 p-4 text-sm text-bloq-muted">
              Cipher Lock status is unavailable right now. Messages are still protected in transit.
            </div>
            <button onClick={onRefresh} className="flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-2 text-xs font-semibold text-bloq-muted transition-colors hover:text-white" data-testid="group-cipher-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
